import React, { useState, useContext, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { AuthContext } from '../../context/AuthContext';
import {
  Form,
  Input,
  Button,
  Checkbox,
  Card,
  Typography,
  Space,
  Layout,
  Divider,
} from 'antd';
import {
  UserOutlined,
  LockOutlined,
  CarOutlined,
  LoginOutlined,
} from '@ant-design/icons';
import { trimFormValues } from '../../utils/trimFormValues';

const { Title, Text } = Typography;
const { Content } = Layout;

const Login = () => {
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated, error, clearError } = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }

    if (error) {
      toast.error(error);
      clearError();
    }
  }, [isAuthenticated, error, navigate, clearError]);

  const onFinish = async (values) => {
    const trimmedValues = trimFormValues(values);
    setLoading(true);
    const result = await login(trimmedValues);
    setLoading(false);
    
    if (result.success) {
      toast.success('Login successful');
      navigate('/');
    } else {
      toast.error(result.error || 'Login failed');
    }
  };

  return (
    <Layout style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      <Content style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        padding: '24px'
      }}>
        <Card
          style={{
            width: '100%',
            maxWidth: '400px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            borderRadius: '8px'
          }}
        >
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div style={{ textAlign: 'center' }}>
              <Space direction="vertical" size="small">
                <CarOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
                <Title level={2} style={{ margin: 0 }}>Jay & EE Logistics</Title>
                <Text type="secondary">Sign in to access your logistics dashboard</Text>
              </Space>
            </div>

            <Form
              name="login"
              onFinish={onFinish}
              layout="vertical"
              size="large"
            >
              <Form.Item
                name="email"
                rules={[
                  { required: true, message: 'Please input your email!' },
                  { type: 'email', message: 'Please enter a valid email!' }
                ]}
              >
                <Input
                  prefix={<UserOutlined />}
                  placeholder="Email"
                  autoComplete="email"
                />
              </Form.Item>

              <Form.Item
                name="password"
                rules={[{ required: true, message: 'Please input your password!' }]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  placeholder="Password"
                  autoComplete="current-password"
                />
              </Form.Item>

              <Form.Item>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Form.Item name="remember" valuePropName="checked" noStyle>
                    <Checkbox>Remember me</Checkbox>
                  </Form.Item>
                  <Link to="/forgot-password">
                    <Text type="link">Forgot password?</Text>
                  </Link>
                </div>
              </Form.Item>

              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<LoginOutlined />}
                  loading={loading}
                  block
                >
                  Sign in
                </Button>
              </Form.Item>
            </Form>

            <Divider style={{ margin: '12px 0' }}>
              <Text type="secondary">or</Text>
            </Divider>

            <Space direction="vertical" size="small" style={{ width: '100%', textAlign: 'center' }}>
              <Text>
                Don't have an account?{' '}
                <Link to="/register">
                  <Text type="link">Sign up</Text>
                </Link>
              </Text>
              <Link to="/server-status">
                <Text type="link">Check Server Status</Text>
              </Link>
            </Space>
          </Space>
        </Card>
      </Content>
    </Layout>
  );
};

export default Login;